import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Container, UncontrolledTooltip } from "reactstrap";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ConfigModal from "Pages/Setup/Configuration/Components/ConfigModal/ConfigModal";
import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Setup/Configuration/Configuration.module.css";
import DefaultColumnFilter from "components/ReactTable/Components/DefaultColumnFilter/DefaultColumnFilter";
import { HideOrShowPassword } from "Pages/Setup/Configuration/Components/HideOrShowPassword/HideOrShowPassword";
import useUserContext from "Hooks/useUserContext";
import useHelpers from "Hooks/useHelpers";
import usePageTitle from "Hooks/usePageTitle";

type ActionType = "new" | "edit" | "delete" | "";
interface IConfiguration {
    keyName: string;
    keyValue: string;
    is_obscured: boolean;
}

export default function Configuration() {
    const [configs, setConfigs] = useState<IConfiguration[]>([]);
    const [activeModal, setActiveModal] = useState<ActionType>("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(undefined);

    usePageTitle("Configuration");
    
    const [userContext] = useUserContext();
    const { get, post } = useFetch();
    const { copyToClipboard } = useHelpers();

    const ignore = useRef(false);

    /** Fetches all config values for a company_id */
    const getConfigs = useCallback(
        async (ignore: React.MutableRefObject<boolean>) => {
            if (ignore.current) return;

            setIsLoading(true);

            const response = await get(
                `/api/configuration/${userContext.companyId}`
            );

            if (response.ok && !ignore.current) {
                const data = await response.json();
                console.log(
                    "Configuration page response from request for configurations in :",
                    data
                );
                setConfigs(data);
            }
            setIsLoading(false);
        },
        [get, userContext.companyId]
    );

    /** Calls the fetch getConfigs function on page load */
    useEffect(() => {
        ignore.current = false;

        getConfigs(ignore);

        return () => {
            ignore.current = true;
        };
    }, [get, getConfigs, post]);

    /** Wraps state setting callback to pass to child component and update state from parent */
    const onSetActiveModal = useCallback((modal: ActionType) => {
        setActiveModal(modal);
    }, []);

    /** Wraps state setting callback to pass to child component and update state from parent */
    function onSetSelectedRow(row: any) {
        setSelectedRow(row);
    }

    /** Passed down to table as rowDoubleClickCallback */
    const editRow = useCallback((row: any) => {
        setActiveModal("edit");
        setSelectedRow(row);
    }, []);

    const columns = useMemo(() => {
        const cellContent = (
            row: number,
            value: string,
            columnName: string
        ) => (
            <>
                <FontAwesomeIcon
                    id={columnName + row}
                    className={styles.copyIcon}
                    onClick={() => copyToClipboard(value)}
                    icon={faCopy}
                />
                <UncontrolledTooltip target={columnName + row}>
                    Copy to clipboard
                </UncontrolledTooltip>
                <span className={styles.textBox}>{value}</span>
            </>
        );

        return [
            {
                Header: "Setting",
                accessor: "keyName",
                Cell: ({ row, value }: { row: any; value: string }) =>
                    cellContent(row.index, value, "keyName"),
                // Not using default filter, so as to allow for auto focusing this input
                Filter: ({ column }: any) => {
                    return <DefaultColumnFilter column={column} autoFocus />;
                },
            },
            {
                Header: "Value",
                accessor: "keyValue",
                Cell: ({ value, row }: { value: string; row: any }) =>
                    isHidden(row)
                        ? HideOrShowPassword(value, row.index)
                        : cellContent(row.index, value, "keyValue"),
            },
            {
                Header: "hidden",
                accessor: "is_obscured",
            },
        ];
    }, [copyToClipboard]);

    const configsWithoutCompanyId = useMemo(() => {
        return configs.map((config: IConfiguration) => {
            const newKeyName = config.keyName.split(".").slice(1).join(".");
            return { ...config, keyName: newKeyName };
        });
    }, [configs]);

    const tableProps = {
        columns,
        data: configsWithoutCompanyId,
        showPagination: true,
        onSetSelectedRow,
        rowDoubleClickCallback: editRow,
        isLoading,
    };

    const configModalProps = {
        activeModal,
        getConfigs,
        configs,
        onSetActiveModal,
        onSetSelectedRow,
        selectedRow,
        ignore,
    };

    return (
        <Container fluid>
            {/* Header and New/Edit/Delete buttons */}
            <div className={styles.headerAndButtonsBox}>
                <h1>Configuration</h1>
                <span className={styles.buttonsBox}>
                    <Button
                        color="primary"
                        onClick={() => setActiveModal("new")}
                    >
                        New
                    </Button>
                    <Button
                        color="success"
                        onClick={() => setActiveModal("edit")}
                    >
                        Edit
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => setActiveModal("delete")}
                    >
                        Delete
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />

            <ConfigModal {...configModalProps} />
        </Container>
    );
}

/** Function to determine if a row should be hidden. Also used in TestConnection.tsx */
export function isHidden(row: any) {
    const hiddenKeynames = ["token", "password", "pwd", "secret", "connectionstring"];

    if (row.original.is_obscured === true) {
        return true;
    }

    for (const key of hiddenKeynames) {
        if (row.original.keyName.toLowerCase().includes(key)) {
            return true;
        }
    }

    return false;
}
